/**
 * Modal — a dialog rendered in a portal on document.body.
 *
 * Handles the things every hand-rolled dialog forgets: Escape to close, a focus trap,
 * focus restored to whatever opened it, body scroll lock, aria-modal wiring, and a
 * backdrop click that only closes on a real backdrop press (not on a drag that started
 * inside the dialog and ended outside it).
 *
 *   <Modal open={open} onClose={close} title="Re-issue link"
 *          footer={<><Button variant="ghost" onClick={close}>Cancel</Button>
 *                   <Button variant="primary" onClick={save}>Re-issue</Button></>}>
 *     …
 *   </Modal>
 *
 * For a yes/no on a destructive action use ConfirmDialog instead — it already has the
 * copy structure and the typed-confirmation option.
 *
 * HEIGHT BUDGET. The dialog is capped at the viewport (styles.css: `.modal` is a flex
 * column with a max-height, `.modal-body` scrolls, head and foot are flex: 0 0 auto), so
 * the title and the footer are always on screen. Before that, a long dialog grew past the
 * viewport and scrolled inside the backdrop, which put Save 201px and Send 435px below the
 * fold — the consequence text and the button that commits to it were never visible at the
 * same time, on dialogs that mint or revoke a live client link.
 */

import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalProps {
  open: boolean;
  /** Called on Escape, backdrop click and the close button. */
  onClose: () => void;
  title: ReactNode;
  /** Sub-line under the title. Say what will happen, not what the dialog is. */
  description?: ReactNode;
  children: ReactNode;
  /** Buttons, right-aligned. Put the confirming action last. */
  footer?: ReactNode;
  size?: ModalSize;
  /** Hide the × — use when the operator must pick one of the footer actions. */
  hideClose?: boolean;
  /** Block Escape/backdrop dismissal while a request is in flight. */
  dismissible?: boolean;
  /** Extra class on the dialog element. */
  className?: string;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  hideClose = false,
  dismissible = true,
  className = '',
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreFocusTo = useRef<HTMLElement | null>(null);
  const backdropPressStartedOnBackdrop = useRef(false);

  const requestClose = useCallback(() => {
    if (dismissible) onClose();
  }, [dismissible, onClose]);

  // Escape + focus trap.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        requestClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, requestClose]);

  // Scroll lock + focus handoff and restore.
  useEffect(() => {
    if (!open) return;

    restoreFocusTo.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTimer = window.setTimeout(() => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      const target = dialog.querySelector<HTMLElement>(FOCUSABLE) ?? dialog;
      // preventScroll: the dialog is already in view inside its own scroll container,
      // and letting focus() scroll would drag the locked page behind it.
      target.focus({ preventScroll: true });
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      restoreFocusTo.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        backdropPressStartedOnBackdrop.current = event.target === event.currentTarget;
      }}
      onMouseUp={(event) => {
        if (event.target === event.currentTarget && backdropPressStartedOnBackdrop.current) {
          requestClose();
        }
        backdropPressStartedOnBackdrop.current = false;
      }}
    >
      <div
        ref={dialogRef}
        className={`modal size-${size} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
      >
        <div className="modal-head">
          <div className="grow">
            <div className="modal-title">{title}</div>
            {description ? <div className="modal-description">{description}</div> : null}
          </div>
          {hideClose ? null : (
            <button
              type="button"
              className="btn btn-ghost btn-sm btn-icon modal-close"
              onClick={requestClose}
              aria-label="Close dialog"
            >
              ✕
            </button>
          )}
        </div>

        <div className="modal-body">{children}</div>

        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

export default Modal;
